import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { User, Check } from 'lucide-react';

const ProfileQuestion = ({ question, onAnswer, isAnswered }) => {
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [textAnswer, setTextAnswer] = useState('');

  const handleSingleSelect = (value) => {
    setSelectedAnswers([value]);
    onAnswer(question.id, value);
  };

  const handleMultipleSelect = (value) => {
    const newAnswers = selectedAnswers.includes(value)
      ? selectedAnswers.filter(a => a !== value)
      : [...selectedAnswers, value];
    
    if (newAnswers.length <= (question.maxSelections || 10)) {
      setSelectedAnswers(newAnswers);
    }
  };

  const handleTextSubmit = () => {
    if (textAnswer.trim()) {
      onAnswer(question.id, textAnswer);
    }
  };

  const handleMultipleSubmit = () => {
    if (selectedAnswers.length > 0) {
      onAnswer(question.id, selectedAnswers);
    }
  };

  if (isAnswered) {
    return (
      <Card className="border-2 border-emerald-700 rounded-sm mb-4 bg-emerald-50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-emerald-700 flex items-center justify-center">
              <Check className="w-5 h-5 text-white" />
            </div>
            <p className="text-sm font-medium text-emerald-900">{question.question}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-amber-500 rounded-sm mb-4 shadow-lg animate-fade-in">
      <CardContent className="p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <User className="w-5 h-5 text-amber-700" />
          </div>
          <div>
            <h4 className="font-semibold text-zinc-900 mb-2">{question.question}</h4>
            <p className="text-sm text-zinc-600">Help us craft the perfect adventure for you</p>
          </div>
        </div>

        {question.type === 'single' && (
          <div className="space-y-2">
            {question.options.map((option) => (
              <button
                key={option.value}
                onClick={() => handleSingleSelect(option.value)}
                className="w-full text-left p-4 border-2 border-zinc-200 rounded-sm hover:border-emerald-700 hover:bg-emerald-50 transition-all duration-200 group"
              >
                <span className="text-zinc-900 group-hover:text-emerald-900">{option.label}</span>
              </button>
            ))}
          </div>
        )}

        {question.type === 'multiple' && (
          <div className="space-y-4">
            <div className="space-y-2">
              {question.options.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleMultipleSelect(option.value)}
                  className={`w-full text-left p-4 border-2 rounded-sm transition-all duration-200 ${
                    selectedAnswers.includes(option.value)
                      ? 'border-emerald-700 bg-emerald-50 text-emerald-900'
                      : 'border-zinc-200 hover:border-amber-500 hover:bg-amber-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      selectedAnswers.includes(option.value)
                        ? 'border-emerald-700 bg-emerald-700'
                        : 'border-zinc-400'
                    }`}>
                      {selectedAnswers.includes(option.value) && (
                        <Check className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <span>{option.label}</span>
                  </div>
                </button>
              ))}
            </div>
            <Button 
              onClick={handleMultipleSubmit}
              disabled={selectedAnswers.length === 0}
              className="w-full bg-emerald-800 hover:bg-emerald-900 text-white py-4 rounded-sm"
            >
              Continue
            </Button>
            {question.maxSelections && (
              <p className="text-xs text-zinc-500 text-center">
                Select up to {question.maxSelections} options
              </p>
            )}
          </div>
        )}

        {question.type === 'text' && (
          <div className="space-y-4">
            <Textarea
              value={textAnswer}
              onChange={(e) => setTextAnswer(e.target.value)}
              placeholder={question.placeholder}
              rows={4}
              className="border-zinc-300 focus:border-emerald-700 rounded-sm"
            />
            <Button 
              onClick={handleTextSubmit}
              disabled={!textAnswer.trim()}
              className="w-full bg-emerald-800 hover:bg-emerald-900 text-white py-4 rounded-sm"
            >
              Continue
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProfileQuestion;